<template>
  <div>
    <transition :name="backdropTransition" appear><div class="gk-modal-backdrop" @click="close"></div></transition>
    <transition :name="mainTransition" appear mode="out-in">
      <div class="gk-modal-wrapper" :key="mainTransitionKey" :class="wrapperClass">
        <div class="gk-master-modal" :class="modalClass">
          <slot name="background" />
          <div class="gk-modal-content">
            <div class="modal-titlebar" :class="[variantClass, { 'has-status': hasStatus, 'has-buttons': hasButtons }]">
              <div class="modal-titlebar--topleft">
                <div class="title-status-wrap">
                  <h2 class="title">
                    <slot name="title" />
                  </h2>
                  <slot name="status" />
                </div>
              </div>
              <div class="modal-titlebar--topright">
                <slot name="options" />
                <slot name="premiumtip" />
                <GekkoButton 
                  @click="close()"
                  icon="fa-regular fa-xmark"
                  variant="link"
                  size="lg"
                  class="close-btn-titlebar"
                />
              </div>
            </div>
            <div class="master-invoice-modal-selector-wrap">
              <div class="master-invoice-modal-selector">
                <slot name="navbar" />
              </div>
              <slot name="topactions" />
            </div>
            <div class="modal-content-wrap" :class="{ 'has-title': hasTitle, 'has-actions': hasActions }">
              <LoadingSpinner v-if="loading" />
              <overlay-scrollbars v-if="overlayScrollb" :options="{ scrollbars: { autoHide: 'never' }, overflowBehavior: { y: 'scroll', x: 'hidden' } }">
                <slot name="maincontent" />
              </overlay-scrollbars>
              <slot v-else name="maincontent" />
            </div>
            <div class="bottom-bar-wrapper" :class="{ 'padded': hasActions }">
              <slot name="prevnext" />
              <slot name="actions" />
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapMutations, mapGetters } from 'vuex'

import LoadingSpinner from "@/components/dashboard/ui/LoadingSpinner"
import GekkoButton from "@/components/GekkoButton"
export default {
  name: "MasterModalBase",
  components: {
    LoadingSpinner,
    GekkoButton
  },
  props: {
    autoH: {
      type: Boolean,
      default: false
    },
    fullH: {
      type: Boolean,
      default: false
    },
    fullAutoH: {
      type: Boolean,
      default: false
    },
    narrow: {
      type: Boolean,
      default: false
    },
    widthMd: {
      type: Boolean,
      default: false
    },
    widthLg: {
      type: Boolean,
      default: false
    },
    widthXl: {
      type: Boolean,
      default: false
    },
    extraNarrow: {
      type: Boolean,
      default: false
    },
    short: {
      type: Boolean,
      default: false
    },
    overlayScrollb: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    fullScreen: {
      type: Boolean,
      default: false
    },
    mobileFullScreen: {
      type: Boolean,
      default: false
    },
    variantClass: {
      type: String,
      default: 'primary'
    },
    actionButtons: {
      type: String,
      default: null
    },
    navigating: {
      type: String,
      default: null
    },
    mainTransitionKey: {
      type: Number,
      default: null
    },
  },
  data () {
    return {
    }
  },
  computed: {
    ...mapGetters({ 
      modal: "modals/getModal",
    }),
    backdropTransition () {
      return (this.navigating) ? 'stay': 'fade'
    },
    mainTransition () {
      let mainTransition = 'pop'
      if (this.navigating && this.navigating == 'prev') {
        mainTransition = 'slideright'
      }
      if (this.navigating && this.navigating == 'next') {
        mainTransition = 'slideleft'
      }
      if (this.navigating && this.navigating == 'no-transition') {
        mainTransition = ''
      }
      return mainTransition
    },
    hasButtons () {
      return !!this.$slots['buttons']
    },
    hasTitle () {
      return !!this.$slots['title']
    },
    hasStatus () {
      return !!this.$slots['status']
    },
    hasActions () {
      return (this.actionButtons && this.actionButtons != '')
    },
    wrapperClass () {
      return [
        (this.onTop) ? 'gk-modal-wrapper-top' : '',
        (this.fullScreen) ? 'gk-modal-fullscreen' : '',
        (this.mobileFullScreen) ? 'gk-modal-mobile-fullscreen' : '',
      ]
    },
    modalClass () {
      return [
        (this.short) ? 'gk-modal-h-sm' : '',
        (this.autoH) ? 'gk-modal-h-auto' : '',
        (this.fullH) ? 'gk-modal-h-full' : '',
        (this.fullAutoH) ? 'gk-modal-h-fullauto' : '',
        (this.narrow) ? 'gk-modal-w-sm' : '',
        (this.widthLg) ? 'gk-modal-w-lg' : '',
        (this.widthMd) ? 'gk-modal-w-md' : '',
        (this.widthXl) ? 'gk-modal-w-xl' : '',
        (this.extraNarrow) ? 'gk-modal-w-xs' : ''
      ]
    }
  },
  created () {
    window.addEventListener('keydown', this.closeEventListener)
    this.setModal(this.$parent.$options.name)
  },
  destroyed () {
    window.removeEventListener('keydown', this.closeEventListener)
    this.setModal(false)
  },
  methods: {
    ...mapMutations({ 
      setModal: 'modals/SET_MASTER_MODAL'
    }),
    closeEventListener (event) {
      if (event.key === 'Escape' && !this.modal) this.close()
    },
    close () {
      document.body.classList.remove('modal-on')
      this.$emit('close-modal')
    }
  }
};
</script>

<style lang="scss">
  .gk-modal-backdrop {
    height: auto;
    background: rgb(0,0,0,0.3);
    position: fixed;
    top: 0;
    left: 0;
    width: auto;
    z-index: 1005;
    right: 0;
    bottom: 0;
  }
  .gk-modal-wrapper {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    padding: 0.5rem;
    z-index: 1010;
    display: flex;
    overflow-y: auto;
    overflow-x: hidden;
    &.gk-modal-wrapper-top {
      .gk-master-modal {
        margin-top: 0;
      }
    }
    @media (min-width: 576px) {
      padding: 1rem;
    }
    .gk-master-modal {
      background: white;
      margin: auto;
      width: 100%;
      height: 100%;
      overflow: hidden;
      transform: none;
      flex: 1 1 auto;
      box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1),0 8px 10px -6px rgb(0 0 0 / 0.1);
      border-radius: $radiusLg;
      max-width: $modalWidthXl;

      position: relative;

      .modal-content-wrap {
        &.has-title { padding-top: 0; }
        &.has-actions { padding-bottom: 0; }
      }

      .bottom-bar-wrapper {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: 0.5rem;
        border-top: 1px solid $colorBorderDash;
        &.padded {
          padding: 0.5rem;
          @media (min-width: 769px) {
            padding: 0.75rem;
          }
        }
        .bottom-action-btns--wrap {
          display: flex;
          flex: 1;
          justify-content: flex-end;
          align-items: center;
          gap: 0.5rem;
          margin-left: auto;
        }
      }

      .gk-modal-content {
        position: relative;
        height: 100%;
        width: 100%;
        display: flex;
        flex-direction: column;
        .modal-content-wrap {
          flex: 1;
          overflow: visible;
          height: calc(100% - 70px);
          &.has-actions {
            height: calc(100% - 145px);
          }
          .os-host { height: 100%; }
          .os-host-overflow-y { padding-right: 0rem; }
        }
      }

      .modal-dropzone-actions {
        display: flex;
        flex-direction: column;
        padding-bottom: 1rem;
        &--wrap {
          height: 100%;
          padding: 10px;
          border-radius: $radiusMd;
          background: $inputBackgroundColor;
          box-shadow: inset 0 1px 1px rgb(0 0 0 / 8%);
          display: block;
        }
        .modal-dropzone-wrapper {
          flex: 1;
          height: 100%;
        }
        .modal-main-actions {
          height: auto;
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          .hint {
            font-size: 14px;
            padding-left: 10px;
            margin-bottom: -3px;
            line-height: 1.2;
          }
        }
        @media (min-width: 576px) {
          padding-bottom: 0px;
        }
      }

    }
  }

  .modal-on, .master-modal-on {
    overflow: hidden;
  }

  .gk-master-modal {
    &.gk-modal-h-auto {
      height: auto !important;
      overflow: visible !important;
    }
    &.gk-modal-h-full {
      display: flex;
      height: auto;
      min-height: 100%;
      .gk-modal-content {
        height: auto;
        flex: 1;
      }
    }
    &.gk-modal-h-fullauto {
      height: auto !important;
      overflow: visible !important;
      max-height: auto !important;
    }

    .gk-modal-label {
      font-size: 1rem;
      margin-bottom: 0.5rem;
      color: $colorTextBase;
      &--small {
        font-size: 14px;
        margin-bottom: 0.25rem;
      }
    }
    .modal-form-legend {
      margin-top: 20px;
      legend {
        font-size: 16px;
        font-family: DINNextLTPro-Medium, sans-serif;
      }
    }
    .modal-form-legend:first-child {
      margin-top: 0px;
    }
    
  }
  .gk-modal-fullscreen {
    .gk-master-modal {
      transition: all 0.3s ease;
    }
    @media (max-width: '1450px') { // calc($modalWidthXl + 150px)
      padding: 0;
      .gk-master-modal {
        max-width: 100% !important;
        max-height: 100% !important;
        width: 100%;
        height: 100%;
        border-radius: 0;
        .titlebar.title-modal {
          border-radius: 0;
        }
      }
    }
  }
  .gk-modal-mobile-fullscreen {
    padding: 0;
    .gk-master-modal {
      width: 100% !important;
      max-width: 100% !important;
      height: auto !important;
      max-height: initial !important;
      min-height: 100% !important;
      border-radius: 0;
      .titlebar.title-modal {
        border-radius: 0;
      }
    }
  }

  .master-invoice-modal-selector-wrap {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    width: 100%;
    border-bottom: 1px solid $colorBorderDash;
    z-index: 1;
    padding: 0 1rem;
    justify-content: space-between;
    align-items: center;
    background: $colorBgGrey;
  }
  .master-invoice-modal-selector {
    display: flex;
    flex: 1;
    flex-wrap: wrap;
    justify-content: flex-start;
    margin-bottom: -1px;
    .buttons-tt-wrap {
      height: 100%;
    }
    .buttons-wrap { 
      display: flex;
      align-items: center;

      height: 36px;
      border: 1px solid transparent;

      &.active {
        border: 1px solid $colorBorderDash;
        border-bottom: 1px solid white;
        border-top-left-radius: $radiusMd;
        border-top-right-radius: $radiusMd;
        background: white;
      }

      &:last-child { flex-grow: 0; }
      span.btn-sep {
        color: $colorBlue;
        font-size: 20px;
        opacity: 0.3;
        margin: 0 1rem;
      }
      .btn {
        width: 100%;
        justify-content: center;
        display: flex;
        align-items: center;
        padding: 0.25rem 1rem !important;
        color: $colorTextBase;
        gap: 0.25rem;
        border-radius: 0;
        height: 100%;
        opacity: 0.5;
        font-size: 14px;
        .svg-inline--fa:not(.gk-badge-icon) {
          margin: 0;
          opacity: 0.7;
          display: none
        }
        &.btn-modal-tab {
          border: none;
          border-bottom: $tabsBorderSize solid transparent;
          color: $colorTextBase;
          &.active {
            border-bottom: $tabsBorderSize solid $colorBrand;
            color: $colorBlue;
            opacity: 1;
          }
          &.disabled {
            background: transparent !important;
          }
        }
      }
    }

    @media (min-width: '576px') {
      .buttons-wrap { 
        .btn {
          width: auto;
          font-size: 1rem;
          span { display: inline; }
          .svg-inline--fa:not(.gk-badge-icon) { opacity: 0.3; }
        }
      }
    }
    @media (min-width: '768px') {
      flex-wrap: nowrap;
    }
  }
  .master-invoice-modal-actions {
    display: flex;
    align-items: center;
    padding-right: 0.5rem;
  }
  .modal-topbar-icon-toggle {
    .dropdown-toggle {
      padding: 0.25rem 0.75rem;
      border-radius: $radiusSm;
      font-size: 16px;
      background: white;
      border: 1px solid #d1d5db;
      color: $colorBrand;
      display: flex;
      height: 30px;
      align-items: center;
      &:hover, &:active {
        background: $colorBgGrey;
        color: $colorBrand;
      }
      >.svg-inline--fa {
        margin: 0;
      }
    }
  }
  .modal-titlebar--topright {
    .topright-btn,
    .close-btn-titlebar {
      color: $colorTextSemi;
      cursor: pointer;
      z-index: 9001;
      padding: 0;
      display: flex;
      border: 0;
      justify-content: center;
      align-items: center;
      height: 15px;
      width: 15px;
    }
  }

</style>

<style lang="scss" scoped>
  .gk-modal-w-xl {
    max-width: $modalWidthXl;
  }
  .gk-modal-w-lg {
    max-width: $modalWidthLg;
  }
  .gk-modal-w-md {
    max-width: $modalWidthMd !important;
  }
  .gk-modal-w-sm {
    max-width: $modalWidthSm !important;
  }
  .gk-modal-w-xs {
    max-width: $modalWidthXs !important;
  }
  .gk-modal-h-sm {
    max-height: 500px !important;
  }
  .gk-modal-h-fullauto {
    max-height: 100% !important;
  }

  .modal-titlebar {
    display: flex; 
    gap: 0.5rem; 
    background: $colorBgGrey;
    z-index: 1;
    &--topleft {
      display: flex;
      flex: 1;
      gap: 0.5rem;
      flex-direction: column;
      .title-status-wrap {
        display: flex;
        gap: 1rem;
        align-items: center;
        padding: 0.75rem 1rem;
        padding-right: 150px; // safe mobile closebtn area
        flex-wrap: wrap;
        h2.title {
          font-size: 22px;
          margin-bottom: 0;
          line-height: 1;
          max-width: 500px;
        }
        @media (min-width: '768px') {
          padding-right: 0px;
          h2.title {
          font-size: 24px;
          }
        }
      }
      &__buttons {
        display: flex;
        flex: 1;
        align-items: center;
        justify-content: center;
        margin-bottom: -$tabsBorderSize;
      }
      @media (min-width: '992px') {
        flex-direction: row;
      }
    }
    &--topright {
      position: absolute;
      top: 0;
      right: 0;
      padding: 1rem;
      display: flex;
      gap: 1rem;
    }
    @media (min-width: '768px') {
      gap: 1rem; 
    }
  }
</style>
